import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { map } from 'rxjs/operators'
import { HelperService } from './helper.service'
import { IDataCardFE, IDataCardNotification, IDataCardWorkOrder } from '../models/data-card.model'
import { Observable } from 'rxjs'
import { environment } from '../../environments/environment'

export type DataCardType = 'VLV' | 'BRK' | 'FD_FE_MOT_NFS' | 'PT' | 'BPI' | 'FGD' | 'BFGI' | 'SDL'
export type DataCardGroupType = 'FE' | 'PT' | 'BPI' | 'FGD' | 'BFGI' | 'SDL'

@Injectable({
  providedIn: 'root',
})
export class DataCardService {
  private apiURL: string = environment.apiUrl + '/data-card'

  constructor(private http: HttpClient) {}

  private getHeaders(): HttpHeaders {
    return new HttpHeaders().set('Content-Type', 'application/json')
  }

  public getTypical(mainObject: string): Observable<{ typical: string | null; mo: string | null }> {
    const url: string = this.apiURL + '/typical?tag=' + encodeURIComponent(mainObject)
    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((resource: { typical: string | null; mo: string | null }) => {
        return resource
      }),
    )
  }

  public getDataCardByGroupType(mainObject: string, groupType: DataCardGroupType): Observable<any> {
    const url: string =
      this.apiURL +
      '/output/type/' +
      encodeURIComponent(groupType) +
      '?tag=' +
      encodeURIComponent(mainObject)
    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((data: any): any => {
        if (data && groupType === 'FE') {
          return this.getParsedDataCard4FE(data as IDataCardFE)
        }
        return data
      }),
    )
  }

  private getParsedDataCard4FE(data: IDataCardFE): IDataCardFE {
    data.TravelTimeLimit_Critical_Safe_State = parseFloat(
      `${data.TravelTimeLimit_Critical_Safe_State}`,
    )
    data.TravelTimeLimit_Warning_Fast_SS = parseFloat(`${data.TravelTimeLimit_Warning_Fast_SS}`)
    data.TravelTimeLimit_Warning_Slow_SS = parseFloat(`${data.TravelTimeLimit_Warning_Slow_SS}`)
    data.TravelTimeLimit_Warning_Slow_NS = parseFloat(`${data.TravelTimeLimit_Warning_Slow_NS}`)
    data.TravelTimeLimit_Warning_Fast_NS = parseFloat(`${data.TravelTimeLimit_Warning_Fast_NS}`)
    return data
  }

  public updateEnrich(mainObject: string, form: any): Observable<any> {
    const url: string = this.apiURL + '/form?tag=' + encodeURIComponent(mainObject)
    return this.http.put(url, form, { headers: this.getHeaders() })
  }

  public getDeviationUrl(mainObject: string): Observable<string> {
    const url: string = this.apiURL + '/deviation-url?tag=' + encodeURIComponent(mainObject)
    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((resource: any) => {
        return resource
      }),
    )
  }

  public updateDeviationUrl(
    mainObject: string,
    data: { url: string },
    groupType: DataCardGroupType,
  ): Observable<{ data: any; type: DataCardGroupType }> {
    const url: string =
      this.apiURL +
      '/deviation-url/' +
      encodeURIComponent(groupType) +
      '?tag=' +
      encodeURIComponent(mainObject)
    return this.http.put(url, data, { headers: this.getHeaders() }).pipe(
      map((resource: { data: any; type: DataCardGroupType }) => {
        return resource
      }),
    )
  }

  public getTravelTimeMonitoringURLParams(mainObject: string): any {
    const timeFrame: Date[] = HelperService.getYearlyTimeFrame(1)
    return {
      filter: JSON.stringify(
        HelperService.getObjectWithoutNullValues({
          tagName: mainObject,
          startTime: timeFrame[0],
          endTime: timeFrame[1],
        }),
      ),
      sda: 1,
    }
  }

  public getDataCardTypeByTypical(typical: string | null): DataCardType | null {
    if (HelperService.isNullOrUndefined(typical)) {
      return null
    }
    typical = typical.toUpperCase()
    // Typical VLV-x2x
    if (typical.indexOf('VLV-') === 0) {
      return 'VLV'
    }
    // Typical BRK-x2x
    if (typical.indexOf('BRK-') === 0) {
      return 'BRK'
    }
    // Typical FD-x1x | Typical FE-*, FWP-*, MOT-*, NFS-*
    if (
      typical.indexOf('FD-') === 0 ||
      typical.indexOf('FD2-') === 0 ||
      typical.indexOf('FE-') === 0 ||
      typical.indexOf('FWP-') === 0 ||
      typical.indexOf('MOT-') === 0 ||
      typical.indexOf('NFS-') === 0
    ) {
      return 'FD_FE_MOT_NFS'
    }
    // Typical IPSW* | IESW* | IEMC*
    if (
      typical.indexOf('IPSW') === 0 ||
      typical.indexOf('IESW') === 0 ||
      typical.indexOf('IEMC') === 0
    ) {
      return 'BPI'
    }
    // Typical IP* (except IPSW*) | IELV* | IFGPR*
    if (
      typical.indexOf('IP') === 0 ||
      typical.indexOf('IEPR') === 0 ||
      typical.indexOf('IELV') === 0 ||
      typical.indexOf('IFGPD') === 0 ||
      typical.indexOf('IFGPR') === 0
    ) {
      return 'PT'
    }
    // Typical IFGFL* | IFGG* | IFGHS*
    if (
      typical.indexOf('IFGFL') === 0 ||
      typical.indexOf('IFGG') === 0 ||
      typical.indexOf('IFGHS') === 0
    ) {
      return 'FGD'
    }
    // Typical IFGSW* | IFGMC* | IFGSM* | IFGHT* | IFGAL*
    if (
      typical.indexOf('IFGSW') === 0 ||
      typical.indexOf('IFGMC') === 0 ||
      typical.indexOf('IFGSM') === 0 ||
      typical.indexOf('IFGHT') === 0 ||
      typical.indexOf('IFGAL') === 0
    ) {
      return 'BFGI'
    }
    // Typical SDL-P* | SDL-E*
    if (typical.indexOf('SDL-P') === 0 || typical.indexOf('SDL-E') === 0) {
      return 'SDL'
    }
    return null
  }

  public getDataCardGroupTypeByType(type: DataCardType | null): DataCardGroupType | null {
    const mapping: any = {
      VLV: 'FE',
      BRK: 'FE',
      FD_FE_MOT_NFS: 'FE',
      PT: 'PT',
      BPI: 'BPI',
      FGD: 'FGD',
      BFGI: 'BFGI',
      SDL: 'SDL',
    }
    return mapping[type] ? (mapping[type] as DataCardGroupType) : null
  }

  public getWorkOrderData(workOrder: string): Observable<IDataCardWorkOrder> {
    const url: string = this.apiURL + '/output/work-order?wo=' + encodeURIComponent(workOrder)
    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((resource: any) => {
        return resource
      }),
    )
  }

  public getNotificationData(notification: string): Observable<IDataCardNotification> {
    const url: string = this.apiURL + '/output/notification?no=' + encodeURIComponent(notification)
    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((resource: any) => {
        return resource
      }),
    )
  }

  public getNotificationsByMainObject(mainObject: string): Observable<IDataCardNotification[]> {
    const url: string =
      this.apiURL + '/output/mo/notifications?mo=' + encodeURIComponent(mainObject)
    return this.http.get(url, { headers: this.getHeaders() }).pipe(
      map((resource: any) => {
        return resource
      }),
    )
  }
}
